import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FilePlus2, Check } from 'lucide-react';
import { CxDialog } from '@/cajita/overlay';
import { getNoteReasons } from './documents.api';

export interface CreditNoteDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  fullNumber: string;
  defaultType?: 'NOTA_CREDITO' | 'NOTA_DEBITO';
  onConfirm: (input: { type: 'NOTA_CREDITO' | 'NOTA_DEBITO'; reasonCode: string; notes?: string }) => void;
  isSubmitting?: boolean;
}

export function CreditNoteDialog({
  open,
  onOpenChange,
  fullNumber,
  defaultType = 'NOTA_CREDITO',
  onConfirm,
  isSubmitting,
}: CreditNoteDialogProps): JSX.Element | null {
  const reasons = useQuery({ queryKey: ['note-reasons'], queryFn: getNoteReasons, staleTime: Infinity });
  const [type, setType] = useState<'NOTA_CREDITO' | 'NOTA_DEBITO'>(defaultType);
  const [reasonCode, setReasonCode] = useState('01');
  const [notes, setNotes] = useState('');

  const list = type === 'NOTA_CREDITO' ? reasons.data?.creditNote : reasons.data?.debitNote;

  if (!open) return null;

  return (
    <CxDialog
      title={`Emitir nota sobre ${fullNumber}`}
      sub="Las líneas se copian del comprobante original. Para ajustes parciales, edita en el POS antes de emitir."
      icon={<FilePlus2 size={20} />}
      iconTone="primary"
      onClose={() => onOpenChange(false)}
      footer={
        <>
          <button className="btn btn-secondary grow" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancelar
          </button>
          <button className="btn btn-primary grow" onClick={() => onConfirm({ type, reasonCode, notes: notes || undefined })} disabled={isSubmitting}>
            <Check size={18} />
            {isSubmitting ? 'Emitiendo…' : 'Emitir nota'}
          </button>
        </>
      }
    >
      <div className="col gap14">
        <div className="field">
          <label className="label">Tipo</label>
          <select
            className="select"
            value={type}
            onChange={(e) => {
              setType(e.target.value as 'NOTA_CREDITO' | 'NOTA_DEBITO');
              setReasonCode('01');
            }}
          >
            <option value="NOTA_CREDITO">Nota de crédito</option>
            <option value="NOTA_DEBITO">Nota de débito</option>
          </select>
        </div>
        <div className="field">
          <label className="label">Motivo</label>
          <select className="select" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
            {(list ?? []).map((r) => (
              <option key={r.code} value={r.code}>
                {r.code} — {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label">Notas (opcional)</label>
          <textarea className="input" value={notes} rows={2} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>
    </CxDialog>
  );
}
